import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface BillingEvent {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  event_type: string;
  amount_cents: number;
  currency: string;
  stripe_invoice_id: string | null;
  description: string | null;
  created_at: string;
}

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "payment_succeeded", label: "Pagamento Aprovado" },
  { value: "payment_failed", label: "Pagamento Falhou" },
  { value: "refund", label: "Reembolso" },
  { value: "subscription_created", label: "Assinatura Criada" },
  { value: "subscription_canceled", label: "Assinatura Cancelada" },
];

const eventTypeBadge: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  payment_succeeded: "default",
  payment_failed: "destructive",
  refund: "secondary",
  subscription_created: "outline",
  subscription_canceled: "destructive",
};

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(cents / 100);
}

export default function AdminBilling() {
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<{ events: BillingEvent[]; total: number }>({
    queryKey: ["admin-billing-events", eventTypeFilter, page],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: {
          action: "list_billing_events",
          event_type: eventTypeFilter === "all" ? undefined : eventTypeFilter,
          page,
          limit: 20,
        },
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    staleTime: 30 * 1000,
  });

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Faturamento</h2>
        <p className="text-muted-foreground">
          Historico de eventos de billing
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-medium">
              Filtrar por tipo
            </CardTitle>
            <Select value={eventTypeFilter} onValueChange={(v) => { setEventTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="hidden sm:table-cell">Data</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="hidden md:table-cell">Invoice Stripe</TableHead>
                      <TableHead className="hidden lg:table-cell">Descricao</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.events ?? []).map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="hidden sm:table-cell whitespace-nowrap">
                          {format(new Date(event.created_at), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{event.user_name || "-"}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-none">
                              {event.user_email}
                            </div>
                            {/* Show date on mobile under user info */}
                            <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                              {format(new Date(event.created_at), "dd/MM/yy HH:mm", {
                                locale: ptBR,
                              })}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={eventTypeBadge[event.event_type] ?? "outline"}>
                            <span className="hidden sm:inline">{event.event_type}</span>
                            <span className="sm:hidden">{event.event_type.split("_").pop()}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono whitespace-nowrap">
                          {formatCurrency(event.amount_cents, event.currency)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell font-mono text-xs">
                          {event.stripe_invoice_id || "-"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[200px] truncate">
                          {event.description || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data?.events ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Nenhum evento encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data && data.total > 20 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">
                    Pagina {page} de {Math.ceil(data.total / 20)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(data.total / 20)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Proxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
